import React, { type ReactNode, Suspense, useCallback, useEffect, useState } from 'react';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { expValEquals } from '@confluence/feature-experiments';

import { AgentSessionsContext, CommentAgentConversationsContext } from './AgentSessionsContext.ts';
import { AgentSessionPollingProvider } from './AgentSessionPollingProvider.tsx';
import { usePageCommentAgentSessionsInner } from './usePageCommentAgentSessions.ts';
import type { CommentAgentSessionMap } from './usePageCommentAgentSessions.ts';

type AgentSessionsProviderProps = {
	pageId: string;
	children: ReactNode;
	/**
	 * Called with the resolved session map whenever it changes. Lets a parent that
	 * mounts this provider also read the sessions in its own render (e.g. to drive a
	 * comment's connector line) without having to consume the context it provides.
	 * Pass a stable callback (a useState setter or a useCallback) to avoid re-fetches.
	 */
	onSessionMapChange?: (map: CommentAgentSessionMap) => void;
	/**
	 * When true on mount, or changed from false to true, schedules bounded discovery retries.
	 * Changing to false cancels pending retries, including event-triggered retries, but leaves
	 * save/recovery event listeners active. Embedded publishers enable this after a local mention.
	 */
	refetchOnMount?: boolean;
};

// Stable empty map so the initial provider value keeps referential equality across renders.
const EMPTY_SESSIONS: CommentAgentSessionMap = new Map();
const EMPTY_CONVERSATIONS: ReadonlyMap<string, string> = new Map();

// A content signature so we only push a new map into context when the data actually
// changes — buildSessionMap returns a fresh Map instance on every render even when
// unchanged, so we can't compare by reference.
function sessionsSignature(map: CommentAgentSessionMap, includeIdentity = false): string {
	return Array.from(map.entries())
		.map(
			([id, session]) =>
				`${id}:${session.conversationId}:${session.state ?? ''}${
					includeIdentity
						? `:${session.agentId ?? ''}:${session.agentName ?? ''}:${session.avatarUrl ?? ''}`
						: ''
				}`,
		)
		.sort()
		.join('|');
}

/**
 * Inner loader — calls useLazyLoadQuery (may suspend or throw). It renders nothing;
 * its only job is to push the resolved session map up to the provider via onLoaded.
 * Because it lives in a Suspense sibling (not wrapping the children), suspending here
 * never blanks the surrounding comment UI.
 */
function AgentSessionsLoader({
	pageId,
	onLoaded,
	refetchOnMount,
}: {
	pageId: string;
	refetchOnMount?: boolean;
	onLoaded: (map: CommentAgentSessionMap, conversations: ReadonlyMap<string, string>) => void;
}) {
	// This surface never deletes the marker (see below), so hasConfirmedNoSessions is unused here.
	const { sessionMap, conversationMap } = usePageCommentAgentSessionsInner(pageId, {
		// Recover save events missed before mounting or while the initial query was suspended.
		refetchOnMount:
			(refetchOnMount ?? true) &&
			expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true),
	});
	// Include identity-only updates for agentic comments while preserving control behavior.
	const signature = sessionsSignature(
		sessionMap,
		expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true),
	);
	const conversationSignature = Array.from(
		conversationMap,
		([id, conversationId]) => `${id}:${conversationId}`,
	).join('|');

	// This surface fetches eagerly and does NOT read the per-user `cc-agent-mention-sessions`
	// marker (only the footer's AgentSessionsFetcher gates on it). We deliberately do not
	// delete that marker here: this provider mounts right after a comment is saved, when the
	// query is briefly empty because the backend hasn't created the session association yet —
	// deleting on that transient empty result would race the post-save refetch and wrongly
	// strip the marker the footer relies on. The footer owns the marker's cleanup.
	useEffect(() => {
		onLoaded(sessionMap, conversationMap);
		// Depend on `signature` (content) rather than `sessionMap` (new ref every render).
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [signature, conversationSignature, onLoaded]);

	return null;
}

/**
 * Non-blocking agent-sessions provider for the comments-panel and inline-comment
 * surfaces.
 *
 * Unlike AgentSessionsFetcher (which suspends its children while the sessions query
 * loads — acceptable for the footer, which gates on the page marker), this renders
 * `children` IMMEDIATELY with an empty map, loads the page's agent sessions in a
 * Suspense sibling, and pushes the result into AgentSessionsContext once resolved.
 * That way the comment list / inline popup never blanks while the (usually
 * Relay-cache-warm) query is in flight.
 *
 * Used per-surface — a separate refetch from the footer surface — and gated by the
 * caller on the cc_ai_agent_mention_from_comment experiment, since the page marker is
 * not threaded to these surfaces. Errors are caught locally and degrade to the empty
 * map, so comments stay fully functional even if the agent-sessions fetch fails.
 */
export function AgentSessionsProvider({
	pageId,
	children,
	onSessionMapChange,
	refetchOnMount,
}: AgentSessionsProviderProps): React.JSX.Element {
	const [sessionMap, setSessionMap] = useState<CommentAgentSessionMap>(EMPTY_SESSIONS);
	const [conversationMap, setConversationMap] = useState(EMPTY_CONVERSATIONS);

	const handleLoaded = useCallback(
		(map: CommentAgentSessionMap, conversations: ReadonlyMap<string, string>) => {
			setSessionMap(map);
			setConversationMap(conversations);
			onSessionMapChange?.(map);
		},
		[onSessionMapChange],
	);

	return (
		<AgentSessionPollingProvider pageId={pageId}>
			<AgentSessionsContext.Provider value={sessionMap}>
				<GenericErrorBoundary attribution={Attribution.COMMENTS} renderOnError={() => null}>
					<Suspense fallback={null}>
						<AgentSessionsLoader
							pageId={pageId}
							onLoaded={handleLoaded}
							refetchOnMount={refetchOnMount}
						/>
					</Suspense>
				</GenericErrorBoundary>
				<CommentAgentConversationsContext.Provider value={conversationMap}>
					{children}
				</CommentAgentConversationsContext.Provider>
			</AgentSessionsContext.Provider>
		</AgentSessionPollingProvider>
	);
}
